import type { VesselPosition, BaseVesselData, StaticVesselData } from '../types/vessel';

// Special vessel name mapping for barges tracked via their tugs
export const SPECIAL_VESSEL_NAMES: Record<string, string> = {
  '7932343': 'Clean Jacksonville',
  '9850197': 'Q-LNG 4000',
  '9944443': 'Clean Canaveral',
};

export function getVesselId(vessel: VesselPosition): string {
  return (
    vessel.IHSLRorIMOShipNo?.toString() ||
    vessel.IMO?.toString() ||
    vessel.IMOShipNo?.toString() ||
    ''
  );
}

export function getVesselName(
  vessel: VesselPosition,
  vesselId: string,
  baseData?: BaseVesselData,
  staticData?: StaticVesselData
): string {
  if (SPECIAL_VESSEL_NAMES[vesselId]) {
    return SPECIAL_VESSEL_NAMES[vesselId];
  }
  return vessel.Name || baseData?.Name || staticData?.Name || 'Unknown';
}

export function timeSince(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  const interval = seconds / 3600;

  if (interval < 48) {
    if (interval >= 1) {
      const hours = Math.floor(interval);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes >= 1) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    return `${seconds} seconds ago`;
  }

  const days = Math.floor(interval / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;

  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

export function formatEta(eta?: string): string {
  if (!eta || eta === 'N/A' || eta.startsWith('9999')) return '-';
  const date = new Date(eta);
  const day = String(date.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes} UTC`;
}

export function getPositionAgeClass(receivedDate?: string): { class: string; status: string } {
  if (!receivedDate) return { class: 'bg-gray-400', status: 'Unknown position age' };

  const hoursDiff = (Date.now() - new Date(receivedDate).getTime()) / (1000 * 60 * 60);
  const timeAgo = timeSince(new Date(receivedDate));

  if (hoursDiff < 6) return { class: 'bg-green-500', status: `Recent position (${timeAgo})` };
  if (hoursDiff < 24) return { class: 'bg-yellow-500', status: `Position is ${timeAgo}` };
  return { class: 'bg-red-500', status: `Old position (${timeAgo})` };
}

export function getFlagCode(
  flagName: string | undefined,
  countries: Array<{ CountryCode: string; CountryName: string }>
): string {
  if (!flagName) return '';
  const name = flagName.replace(/\(.*?\)/, '').trim().toLowerCase();
  if (name.includes('hong kong')) return 'hk';
  if (name.length === 2) return name;

  const country = countries.find((c) => c.CountryName.toLowerCase() === name);
  return country?.CountryCode.toLowerCase() || '';
}

export function hasValidCoordinates(coordinates: {
    Lat?: number | null;
    Lon?: number | null;
}): boolean {
  if (coordinates.Lat == null || coordinates.Lon == null) {
    return false;
  }

  if (coordinates.Lat < -90 || coordinates.Lat > 90) {
    return false;
  }

  if (coordinates.Lon < -180 || coordinates.Lon > 180) {
    return false;
  }

  return true;
}
